import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLoginStatus, setUser } from '../redux/users/reducer';
import '../styles/Login.css';
import { Button, Container, Grid } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';

export default function Register() {
  const [errorCount, setErrorCount] = useState(0);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.users.user);

  const isLoggedIn =
    useSelector((state) => state.users.isLoggedIn) &&
    localStorage.getItem('token') !== null;

  const formik = useFormik({
    initialValues: {
      username: '',
      pin: '',
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .email('Invalid email format')
        .required('Please enter your email address.'),
      pin: Yup.string()
        .required(
          "You can find your PIN on Dayforce app. Go to 'Profile' -> 'My Profile' -> 'Employee Number' under 'WORK INFORMATION'."
        )
        .matches(/^\d{4}$/, 'PIN must be exactly 4 digits.'),
      firstName: Yup.string().required('First name is required.'),
      lastName: Yup.string().required('Last name is required.'),
      password: Yup.string()
        .required('Password is required.')
        .min(4, 'Password must be at least 4 characters long.'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match') // Ensure passwords match
        .required('Please confirm your password.'),
    }),

    onSubmit: async (values) => {
      try {
        const response = await fetch(
          'https://api.innofitness.ca/users/register',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(values),
          }
        );

        if (response.ok) {
          const createdUser = await response.json();
          const loginResponse = await axios.post(
            'https://api.innofitness.ca/login',
            {
              username: values.username,
              password: values.password,
            }
          );
          const token = loginResponse.headers['access'];
          if (token) localStorage.setItem('token', token);
          dispatch(setLoginStatus(true));
          dispatch(setUser(createdUser));
          navigate('/agreement');
        } else {
          setErrorCount(errorCount + 1);
          window.alert(
            "ERROR: Unable to sign you up.\nPlease make sure you haven't already registered on this website."
          );
        }
      } catch (error) {
        console.error('Error registering: ', error);
      }
    },
  });

  useEffect(() => {
    if (user && isLoggedIn) {
      navigate('/profile');
    }
  });

  return (
    <Container
      className="page-container"
      sx={{ maxWidth: '1200px', px: 'max(2%, 16px) !important' }}
    >
      <div className="page-title page-title-register">
        <h1 style={{ textAlign: 'center' }}>Register to Join Inno Fitness!</h1>
        <p style={{ maxWidth: '90%', margin: '0 auto' }}>
          Please note that agreeing to our agreement is required prior to
          booking.
        </p>
      </div>

      <Grid className="grid-container" sx={{ gap: '15px' }}>
        <div style={{ margin: '0 auto' }}>
          <div className="field-container">
            <span className="login-label">
              <label htmlFor="username">
                <b>Email:</b>
              </label>
            </span>
            <span>
              <input
                type="email"
                id="username"
                name="username"
                value={formik.values.username}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                required
              />
            </span>
          </div>
          {formik.touched.username && formik.errors.username && (
            <div className="error">{formik.errors.username}</div>
          )}
        </div>

        <div className="field-container-wrap">
          <div className="field-container">
            <span className="login-label">
              <label htmlFor="pin">
                <b>PIN:</b>
              </label>
            </span>
            <span>
              <input
                type="text"
                id="pin"
                name="pin"
                value={formik.values.pin}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                required
              />
            </span>
          </div>
          {formik.touched.pin && formik.errors.pin && (
            <div className="error">{formik.errors.pin}</div>
          )}
        </div>

        <div className="field-container-wrap">
          <div className="field-container">
            <span className="login-label">
              <label htmlFor="firstName">
                <b>First Name:</b>
              </label>
            </span>
            <span>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                required
              />
            </span>
          </div>
          {formik.touched.firstName && formik.errors.firstName && (
            <div className="error">{formik.errors.firstName}</div>
          )}
        </div>

        <div className="field-container-wrap">
          <div className="field-container">
            <span className="login-label">
              <label htmlFor="lastName">
                <b>Last Name:</b>
              </label>
            </span>
            <span>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                required
              />
            </span>
          </div>
          {formik.touched.lastName && formik.errors.lastName && (
            <div className="error">{formik.errors.lastName}</div>
          )}
        </div>

        <div className="field-container-wrap">
          <div className="field-container">
            <span className="login-label">
              <label htmlFor="password">
                <b>Password:</b>
              </label>
            </span>
            <span>
              <input
                type="password"
                id="password"
                name="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                required
              />
            </span>
          </div>
          {formik.touched.password && formik.errors.password && (
            <div className="error">{formik.errors.password}</div>
          )}
        </div>

        <div className="field-container-wrap">
          <div className="field-container">
            <span className="login-label">
              <label htmlFor="confirmPassword">
                <b>Confirm Password:</b>
              </label>
            </span>
            <span>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                required
              />
            </span>
          </div>
          {formik.touched.confirmPassword && formik.values.confirmPassword && (
            <div
              className={
                formik.values.confirmPassword === formik.values.password
                  ? 'success'
                  : 'error'
              }
            >
              {formik.values.confirmPassword === formik.values.password
                ? 'Passwords match!'
                : 'Passwords do not match.'}
            </div>
          )}
        </div>

        <div className="field-container-wrap">
          <div className="field-container">
            <span className="login-label">
              <label htmlFor="jokguTeam">
                <b>Team:</b>
              </label>
            </span>
            <span>
              <select
                id="jokguTeam"
                name="jokguTeam"
                value={formik.values.jokguTeam}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                defaultValue={''}
              >
                <option value="">I don't play Jokgu</option>
                <option value="HI_KICK">Hi Kick (Women)</option>
                <option value="TRINITY">Trinity (L3)</option>
                <option value="SAVAGE_WOLVES">Savage Wolves (L2)</option>
                <option value="LION">LION (L1)</option>
                <option value="SOOJOKGU">SOOJOKGU (Warehouse)</option>
                <option value="LOVELYZ">Lovelyz (Managers)</option>
                <option value="CONTROL">Control (QA/QC)</option>
                <option value="MFA">M.F A (Fabshop A)</option>
                <option value="MFB">M.F B (Fabshop B)</option>
                <option value="UBC">UBC (Office)</option>
                <option value="LABSTERS">Labsters (RnD)</option>
                <option value="WCLE">Tigerwoods (Woodshop)</option>
                <option value="ACCOUNTING">Accounting (Accounting)</option>
                <option value="SALES">Hunters (Sales)</option>
              </select>
            </span>
          </div>
        </div>
      </Grid>

      <Grid
        direction="row"
        sx={{
          display: 'flex',
          pt: '20px',
          mx: 'auto',
          gap: { xs: '5px', sm: '15px' },
        }}
      >
        <Button
          variant="outlined"
          color="black"
          onClick={() => {
            navigate('/');
          }}
          sx={{
            width: { xs: '170px', sm: '200px' },
            mx: 'auto',
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
          }}
        >
          Back to Homepage
        </Button>
        <Button
          variant="outlined"
          onClick={formik.handleSubmit}
          sx={{
            width: { xs: '85px', sm: '100px' },
            mx: 'auto',
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
          }}
        >
          Sign Up
        </Button>
      </Grid>

      {errorCount >= 2 && (
        <p style={{ textAlign: 'center', color: 'red', marginTop: '24px' }}>
          Trouble signing up?
          <br />
          Please let{' '}
          <a
            href="https://teams.microsoft.com/l/chat/0/0?users=jiyoung.kim@innofoods.ca"
            title="jiyoung.kim@innofoods.ca"
            target="_blank"
            rel="noopener noreferrer"
          >
            Ji Young
          </a>{' '}
          know!
        </p>
      )}
    </Container>
  );
}
